import apiClient from './apiClient';

// Cache for maintenance status
let maintenanceCache = {
    inMaintenance: false,
    lastChecked: null,
    ttl: 100000, // 100 segundos cache TTL  
    bypassValidated: false
};

/**
 * Service to manage maintenance mode functionality
 */
const maintenanceService = {
    /**
     * Check if the site is in maintenance mode
     * @param {boolean} forceCheck - Force a server check even if cache is valid
     * @returns {Promise<boolean>} - Whether the site is in maintenance mode
     */
    async isInMaintenance(forceCheck = false) {
        // SOLO verificación de ADMIN (no importa si tiene token o no)
        // IMPORTANTE: Los admins NUNCA deberían ver el modo mantenimiento
        const isAdmin = localStorage.getItem('isAdmin') === 'true';
        
        // NUEVA LÓGICA: SOLO los admins pueden ignorar el mantenimiento
        // Los usuarios normales con token NO deben ignorar el mantenimiento
        if (isAdmin) {
            console.log('[Maintenance] Usuario es admin, ignorando modo mantenimiento');
            localStorage.setItem('maintenance_mode', 'false'); // Forzar desactivación
            return false; // Administradores nunca ven mantenimiento
        }
        
        const now = new Date().getTime();

        // OPTIMIZACIÓN: Verificar primero si hay datos en localStorage para evitar acceso a memoria
        const cachedMaintenanceMode = localStorage.getItem('maintenance_mode');
        const cachedMaintenanceChecked = localStorage.getItem('maintenance_checked');
        
        // Si tenemos datos en localStorage y no ha expirado (usando TTL desde cache)
        if (!forceCheck && cachedMaintenanceMode && cachedMaintenanceChecked) {
            const lastCheckedTime = parseInt(cachedMaintenanceChecked, 10);
            
            // Verificar si el cache todavía es válido usando el TTL configurado
            if (now - lastCheckedTime < maintenanceCache.ttl) {
                // Pero SIEMPRE ignorar caché para admins
                if (isAdmin) {
                    return false;
                }
                
                // Actualizar el objeto de caché en memoria con los valores de localStorage
                maintenanceCache.inMaintenance = cachedMaintenanceMode === 'true';
                maintenanceCache.lastChecked = new Date(parseInt(cachedMaintenanceChecked, 10));
                
                console.log(`[Maintenance] Usando cache de mantenimiento (expira en ${Math.round((maintenanceCache.ttl - (now - lastCheckedTime))/1000)}s)`);
                return maintenanceCache.inMaintenance;
            } else {
                console.log('[Maintenance] Cache de mantenimiento expirado, consultando servidor');
            }
        }
        
        try {
            const result = await this.getMaintenanceStatus();
            maintenanceCache.inMaintenance = result.maintenance_mode;
            maintenanceCache.lastChecked = new Date();
            
            // Guardar en localStorage con timestamp para acceso rápido
            localStorage.setItem('maintenance_mode', result.maintenance_mode ? 'true' : 'false');
            localStorage.setItem('maintenance_checked', now.toString());
            localStorage.setItem('maintenance_ttl', maintenanceCache.ttl.toString());
            
            return maintenanceCache.inMaintenance;
        } catch (error) {
            console.error('Error checking maintenance status:', error);
            // On error, use the last known state or assume not in maintenance
            return maintenanceCache.inMaintenance || false;
        }
    },
    
    /**
     * Get maintenance status details
     * @returns {Promise<Object>} - Maintenance status details
     */
    async getMaintenanceStatus() {
        try {
            // Get authentication token
            const token = localStorage.getItem('accessToken');
            
            // Use a short timeout for this request to avoid long waits
            const response = await apiClient.get('/maintenance/status/', {
                timeout: 5000,
                timeoutSet: true,
                headers: token ? { 
                    'Authorization': `Bearer ${token}`
                } : {}
            });
            return response.data;
        } catch (error) {
            // Si el error es 503, estamos en mantenimiento
            if (error.response && error.response.status === 503) {
                console.log('[Maintenance] Estado de mantenimiento desde error response:', error.response.data);
                return {
                    maintenance_mode: true,
                    message: error.response.data?.message || 'Site is under maintenance',
                    has_bypass_password: error.response.data?.has_bypass || false
                };
            }
            
            // Si hay otro error, loguear y propagar
            console.error('[Maintenance] Error al obtener estado de mantenimiento:', error);
            throw error;
        }
    },
    
    /**
     * Get the last known maintenance state from cache
     * Useful when there's an error fetching the current state
     * @returns {Object} - Last known maintenance state
     */
    getLastKnownMaintenanceState() {
        return {
            inMaintenance: maintenanceCache.inMaintenance,
            lastChecked: maintenanceCache.lastChecked
        };
    },
    
    /**
     * Checks if a user should be allowed to bypass maintenance mode
     * @param {boolean} isAdmin - Whether the user is an admin
     * @returns {Promise<boolean>} - Whether the user can bypass maintenance
     */
    async canBypassMaintenance(isAdmin) {
        // Admins can always bypass
        if (isAdmin) return true;
        
        // Check if the user has validated a bypass password
        return this.hasValidatedBypass();
    },
    
    /**
     * Check if the site has a bypass password configured
     * @returns {Promise<boolean>} - Whether the site has a bypass password
     */
    async hasBypassPassword() {
        try {
            const result = await this.getMaintenanceStatus();
            return result.has_bypass_password || false;
        } catch (error) {
            console.error('Error checking bypass password status:', error);
            return false;
        }
    },
    
    /**
     * Reset cache and check maintenance status
     * This is useful after logging in/out or changing maintenance settings
     * @param {boolean} preserveBypass - Whether to preserve bypass validation status
     */
    resetMaintenanceCache(preserveBypass = true) {
        const bypassValidated = preserveBypass ? maintenanceCache.bypassValidated : false;
        
        // Limpiar localStorage para forzar nueva consulta
        localStorage.removeItem('maintenance_mode');
        localStorage.removeItem('maintenance_checked');
        
        maintenanceCache = {
            inMaintenance: false,
            lastChecked: null,
            ttl: 300000, // Mantener el mismo TTL de 5 minutos
            bypassValidated
        };
        
        // Return a promise that resolves when the new status is fetched
        return this.isInMaintenance(true);
    },
    
    /**
     * Validate a bypass password for maintenance mode
     * @param {string} password - The password to validate
     * @param {number} expiryHours - Hours until the bypass validation expires (default: 12)
     * @returns {Promise<object>} - Response with validation result
     */
    async validateBypassPassword(password, expiryHours = 12) {
        try {
            // Get authentication token if available
            const token = localStorage.getItem('accessToken');
            
            const response = await apiClient.post('/maintenance/validate-bypass/', 
                { password },
                token ? { 
                    headers: { 'Authorization': `Bearer ${token}` } 
                } : {}
            );
            
            if (response.data.valid) {
                // Store bypass validation in cache and localStorage with expiry time
                const expiryTime = new Date();
                expiryTime.setHours(expiryTime.getHours() + expiryHours);
                
                localStorage.setItem('maintenance_bypass', 'true');
                localStorage.setItem('maintenance_bypass_expiry', expiryTime.getTime().toString());
                localStorage.setItem('maintenance_bypass_timestamp', Date.now().toString());
                
                // Actualizar el caché local y forzar actualización
                maintenanceCache.bypassValidated = true;
                maintenanceCache.lastChecked = new Date(); // Actualizar timestamp de última verificación
            }
            
            return response.data;
        } catch (error) {
            console.error('Error validating bypass password:', error);
            return { valid: false, message: 'Error validating password' };
        }
    },
    
    /**
     * Checks if user has validated the bypass password or is an admin
     * @returns {boolean} - Whether the user has bypass access
     */
    hasValidatedBypass() {
        console.log("[Maintenance] Verificando bypass de mantenimiento...");
        
        // PRIORIDAD ABSOLUTA: VERIFICAR ADMIN
        const isAdmin = localStorage.getItem('isAdmin') === 'true';
        
        // PRIORIDAD ABSOLUTA: Si es admin, SIEMPRE permitir acceso (no importa si tiene token)
        if (isAdmin) {
            console.log("[Maintenance] ✅ Usuario es admin, SIEMPRE permitiendo acceso");
            // Forzar desactivación de mantenimiento para admins
            localStorage.setItem('maintenance_mode', 'false');
            return true;
        }
        
        // Si no es admin o no tiene token, verificar bypass password
        const bypassValidated = localStorage.getItem('maintenance_bypass') === 'true';
        
        if (bypassValidated) {
            // Verificar expiración del bypass
            const bypassExpiry = localStorage.getItem('maintenance_bypass_expiry');
            if (bypassExpiry) {
                const expiryTime = parseInt(bypassExpiry, 10);
                const now = Date.now();
                
                if (now < expiryTime) {
                    console.log("[Maintenance] Usuario tiene bypass válido, permitiendo acceso");
                    return true;
                }
                
                // Limpiar bypass expirado
                console.log("[Maintenance] Bypass expirado, limpiando");
                this.clearBypassValidation();
                return false;
            }
            return true;
        }
        
        console.log("[Maintenance] No hay bypass validado");
        return false;
    },
    
    /**
     * Clear the bypass validation
     */
    clearBypassValidation() {
        localStorage.removeItem('maintenance_bypass');
        localStorage.removeItem('maintenance_bypass_expiry');
        localStorage.removeItem('maintenance_bypass_timestamp');
        maintenanceCache.bypassValidated = false;
    }
};

// Función para forzar un reinicio completo del estado de mantenimiento
// Esta función es útil para corregir problemas de acceso de administradores
maintenanceService.forceResetMaintenanceState = async () => {
    try {
        console.log('[Maintenance] Forzando reinicio completo del estado de mantenimiento');
        
        // 1. Verificar si el usuario es admin con el servidor
        const token = localStorage.getItem('accessToken');
        if (!token) {
            console.log('[Maintenance] No hay token, no se puede forzar reinicio');
            return false;
        }
        
        // 2. Hacer una petición directa al servidor para verificar admin
        const response = await fetch('http://10.122.7.101:8000/api/auth/verify-admin/', {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json'
            }
        });
        
        if (!response.ok) {
            throw new Error(`Error del servidor: ${response.status}`);
        }
        
        const data = await response.json();
        const isAdminFromServer = data.is_admin === true;
        
        // 3. Forzar valores correctos en localStorage
        localStorage.setItem('isAdmin', isAdminFromServer ? 'true' : 'false');
        
        if (isAdminFromServer) {
            console.log('[Maintenance] Usuario es admin, desactivando mantenimiento');
            localStorage.setItem('maintenance_mode', 'false');
        } else {
            // Si no es admin, asegurarse de que vea la página de mantenimiento si corresponde
            try {
                // Hacer una verificación fresca del estado de mantenimiento
                const response = await fetch('http://10.122.7.101:8000/api/maintenance/status/', {
                    headers: {
                        'Accept': 'application/json'
                    }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.maintenance_mode) {
                        console.log('[Maintenance] Usuario no es admin y sitio en mantenimiento. Redirigir a página de mantenimiento.');
                        window.location.href = '/mantenimiento';
                    }
                }
            } catch (e) {
                console.error('[Maintenance] Error verificando estado de mantenimiento durante reset:', e);
            }
        }
        
        // 4. Limpiar otros estados de mantenimiento
        localStorage.removeItem('maintenance_bypass');
        localStorage.removeItem('maintenance_bypass_expiry');
        
        // 5. Reiniciar caché local y localStorage
        localStorage.removeItem('maintenance_mode');
        localStorage.removeItem('maintenance_checked');
        
        maintenanceCache = {
            inMaintenance: false,
            lastChecked: null,
            ttl: 300000, // Mantener consistente con el TTL definido (5 minutos)
            bypassValidated: false
        };
        
        console.log('[Maintenance] Reinicio forzado completado');
        return true;
    } catch (error) {
        console.error('[Maintenance] Error al forzar reinicio:', error);
        return false;
    }
};

export default maintenanceService;